import { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import PostLessonInvitation from "../components/PostLessonInvitation";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { trackLessonCompleted } from "@/lib/analytics";

const formatLessonContent = (content) => {
  if (!content) return '';
  
  return content
    // Títols principals (# Títol)
    .replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold text-teal-200 mb-6 mt-8">$1</h1>')
    // Títols secundaris (## Títol)
    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-bold text-teal-300 mb-4 mt-8">$1</h2>')
    // Títols terciaris (### Títol)
    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-teal-400 mb-3 mt-6">$1</h3>')
    // Negreta (**text**)
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>')
    // Cursiva (*text* o _text_)
    .replace(/\*(.+?)\*/g, '<em class="text-gray-300 italic">$1</em>')
    .replace(/_(.+?)_/g, '<em class="text-gray-300 italic">$1</em>')
    // Llistes amb bullets (- Item)
    .replace(/^- (.+)$/gm, '<li class="ml-6 mb-2">$1</li>')
    // Llistes numerades (1. Item)
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-6 mb-2">$1</li>')
    // Paràgrafs (doble salt de línia)
    .replace(/\n\n/g, '</p><p class="mb-4">')
    // Salt de línia simple
    .replace(/\n/g, '<br />')
    // Envoltar tot en paràgrafs
    .replace(/^(.)/g, '<p class="mb-4">$1')
    .replace(/(.)\s*$/, '$1</p>');
};

const LessonDetail = () => {
  const { id } = useParams();
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  const t = (es, ca) => language === 'ca' ? ca : es;

  useEffect(() => {
    fetchLesson();
  }, [id]);

  const fetchLesson = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const [lessonRes, progressRes] = await Promise.all([
        axios.get(`${API}/lessons/${id}`, { headers }),
        axios.get(`${API}/progress`, { headers })
      ]);

      setLesson(lessonRes.data);
      setCompleted(progressRes.data.lessons_completed?.includes(id));
    } catch (error) {
      console.error("Error fetching lesson:", error);
      toast.error("Error al cargar lección");
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/lessons/${id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompleted(true);
      trackLessonCompleted(id, lesson?.title_ca || lesson?.title_es);
      toast.success(language === "es" ? "¡Lección completada!" : "Lliçó completada!");
      // NO auto-retorn - l'usuari decideix quan tornar
    } catch (error) {
      console.error("Error completing lesson:", error);
      toast.error("Error al completar lección");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">Cargando...</div>
        </div>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-gray-400">Lección no encontrada</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          data-testid="back-btn"
          onClick={() => navigate("/lessons")}
          className="flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t("Volver a Lecciones", "Tornar a Lliçons")}</span>
        </button>

        <div data-testid="lesson-detail" className="glass rounded-3xl p-8 border border-teal-400/20 fade-in">
          <h1 className="text-3xl sm:text-4xl font-bold mb-6 glow-text">
            {language === "ca" ? lesson.title_ca : lesson.title_es}
          </h1>

          <div className="prose prose-invert prose-lg max-w-none mb-8 lesson-content">
            <div 
              className="text-gray-200 leading-relaxed"
              dangerouslySetInnerHTML={{ 
                __html: formatLessonContent(language === "ca" ? lesson.content_ca : lesson.content_es)
              }}
            />
          </div>

          {/* Decorative separator */}
          <div className="mt-12 mb-4 flex items-center justify-center">
            <div className="h-px bg-gradient-to-r from-transparent via-purple-400/30 to-transparent w-full max-w-md"></div>
          </div>

          {/* Post-Lesson Invitation in Purple */}
          <PostLessonInvitation 
            language={language} 
            lessonId={id} 
            lessonTitle={language === "ca" ? lesson.title_ca : lesson.title_es}
          />

          <div className="mt-8">
            {!completed ? (
              <button
                data-testid="complete-lesson-btn"
                onClick={handleComplete}
                className="btn-primary px-8 py-3 flex items-center gap-2"
              >
                <CheckCircle2 className="w-5 h-5" />
                <span>{t("Marcar como Completada", "Marcar com a Completada")}</span>
              </button>
            ) : (
              <div data-testid="lesson-completed-badge" className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="w-6 h-6" />
                <span className="text-lg font-semibold">{t("¡Completada!", "Completada!")}</span>
              </div>
            )}
          </div>

          {/* Botó Torna a Lliçons també a BAIX */}
          <button
            data-testid="back-btn-bottom"
            onClick={() => navigate("/lessons")}
            className="flex items-center gap-2 text-gray-400 hover:text-teal-300 transition-colors mt-8"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{t("Volver a Lecciones", "Tornar a Lliçons")}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LessonDetail;